import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ToastService } from '@tylertech/forge-angular';
import { CaptchaService } from '../services/captcha.service';

const TOAST_DURATION = 6000;

// Response codes emitted by the backend bot-gate / rate limiter.
const CODE_CAPTCHA_REQUIRED = 'CAPTCHA_REQUIRED';
const CODE_TEMPORARILY_BLOCKED = 'TEMPORARILY_BLOCKED';

// Header carrying the solved Cloudflare Turnstile token. The backend verifies
// it server-side against Cloudflare's siteverify endpoint (see bot-gate
// verifyTurnstile).
const TURNSTILE_TOKEN_HEADER = 'cf-turnstile-response';

// Header signalling the user dismissed the challenge dialog. A cancel sends
// nothing on its own, so we re-fire the gated request with this header set so
// the backend can count repeated cancels toward a temporary block (see bot-gate
// isCaptchaCancelled / recordCaptchaFailure).
const TURNSTILE_CANCELLED_HEADER = 'cf-turnstile-cancelled';

const DEFAULT_MESSAGE = 'You are making requests too quickly. Please wait a moment and try again.';

const MESSAGES_BY_CODE: Record<string, string> = {
  [CODE_TEMPORARILY_BLOCKED]:
    'Access has been temporarily blocked due to suspicious activity. Please try again later.'
};

/**
 * Handles HTTP 429 responses from the rate limiter / bot-gate:
 *   - CAPTCHA_REQUIRED    -> present the Cloudflare Turnstile challenge; on
 *                            solve, retry the original request once with the
 *                            Turnstile token header.
 *   - TEMPORARILY_BLOCKED -> firm "blocked, try later" toast.
 *   - RATE_LIMIT_EXCEEDED -> generic "slow down" toast.
 * Non-CAPTCHA cases rethrow so per-caller logic (loading state, error page)
 * still runs. CAPTCHA retries suppress the original error on success.
 */
@Injectable({
  providedIn: 'root'
})
export class RateLimitInterceptor implements HttpInterceptor {
  private _lastToastAt = 0;

  constructor(
    private readonly _toastService: ToastService,
    private readonly _captchaService: CaptchaService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 429) {
          const code: string | undefined = error.error?.code;

          // Only escalate to a CAPTCHA when the backend asks for one, and only
          // if this request hasn't already been retried with a token.
          if (code === CODE_CAPTCHA_REQUIRED && !request.headers.has(TURNSTILE_TOKEN_HEADER)) {
            return this.solveAndRetry(request, next, error);
          }

          this.showToast(error);
        }
        return throwError(() => error);
      })
    );
  }

  private solveAndRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    return from(this._captchaService.solve()).pipe(
      switchMap((token) => {
        if (!token) {
          // User cancelled (or the CAPTCHA was unavailable). Re-fire the request
          // with the cancelled-signal header so the backend can count repeated
          // cancels toward a temporary block. next.handle here continues down the
          // chain and does NOT re-enter this interceptor, so the dialog is not
          // reopened. We ignore the signal's own response (or surface a block
          // toast) and always rethrow the original error for per-caller logic.
          const cancelled = request.clone({ setHeaders: { [TURNSTILE_CANCELLED_HEADER]: '1' } });
          return next.handle(cancelled).pipe(
            catchError((signalError: unknown) => {
              // Show the firm block toast only if this cancel crossed the
              // threshold; otherwise stay quiet, as a plain cancel did before.
              if (signalError instanceof HttpErrorResponse &&
                  signalError.error?.code === CODE_TEMPORARILY_BLOCKED) {
                this.showToast(signalError);
              }
              return throwError(() => originalError);
            })
          );
        }
        const retried = request.clone({ setHeaders: { [TURNSTILE_TOKEN_HEADER]: token } });
        return next.handle(retried);
      })
    );
  }

  private showToast(error: HttpErrorResponse): void {
    // Debounce so a burst of rejected requests doesn't stack duplicate toasts.
    const now = Date.now();
    if (now - this._lastToastAt < TOAST_DURATION) {
      return;
    }
    this._lastToastAt = now;

    const code: string | undefined = error.error?.code;
    const message = (code && MESSAGES_BY_CODE[code]) || error.error?.message || DEFAULT_MESSAGE;

    const toast = this._toastService.show({
      message,
      actionHandler: () => toast.close(),
      placement: 'top',
      duration: TOAST_DURATION
    });
  }
}